import './styles.scss';
import React, {Dispatch, useCallback, useEffect, useRef, useState} from "react";

/* types, constants, utils */
import * as Constants from "../../../../../constants";

/* bootstrap */
import {Button} from "react-bootstrap";

/* api */
import {useQuery} from "react-query";
import SearchService from "../../../../../api/search";

/* components */
import ViewerCard from "../../../viewer-card";
import Spinner from "../../../../util/spinner";
import {BrowseQuery} from "../../../../../api/browse.js";
import {BatesQuery} from "../../../../../api/bates.js";
import BateBrowsePaging from "./list-paging";

import {useDispatch, useSelector} from "react-redux";
import {IViewerState} from "../../../../../types/redux/viewer";
import {RootState} from "../../../../../redux";
import useRecordListItemSelect from "../../../hooks/useInsertDBFields";
import {IResultsState} from "../../../../../types/redux/result";
import {AnyAction} from "@reduxjs/toolkit";
import {clearResults, setResults} from "../../../../../redux/slices/result-slice";

interface IProps {
  setNumOfRecords(value:number): void;
}

const ViewerBatesBrowseList = (props: IProps):JSX.Element => {

  const {
    setNumOfRecords
  } = props;

  const dispatch:Dispatch<AnyAction> = useDispatch();
  const viewerState: IViewerState = useSelector<RootState, IViewerState>(state => state.viewer);
  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);

  const initialRender = useRef(false)

  const [currPage, setCurrPage] = useState(0)

  const [prevDisabled, setPrevDisabled] = useState<boolean>(true);
  const [nextDisabled, setNextDisabled] = useState<boolean>(true);


  const [loading, setLoading] = useState(true);
  const [numberingOffset, setNumberingOffset] = useState(0)

  const cardRef = useRef<HTMLInputElement | null>(null);
  const cardContainerRef = useRef<HTMLInputElement | null>(null);
  const resultsContainer = useRef<HTMLInputElement | null>(null);

  const handleSelectRecord = useRecordListItemSelect().handleSelectRecord;

  /* Previous */
  const {
    isRefetching: prevIsFetching,
    refetch: prevRefetch,
    isSuccess: prevIsSuccess,
    data: prevData,
  } = useQuery<any, Error>(
    [viewerState.recordTool + '_prev_' + viewerState.viewerRecord + currPage],
    async ({signal}) => {
      let query: string;
      if (viewerState.recordTool === Constants.BROWSE) {
        query = BrowseQuery(viewerState.initialRecord.collectioncode, Constants.VW_NUM_ROWS, Constants.PREV, viewerState.initialRecord.id_int + ( currPage * Constants.VW_NUM_ROWS));
      } else {
        query = BatesQuery(viewerState.initialRecord.collectioncode, Constants.VW_NUM_ROWS, Constants.PREV, viewerState.initialRecord.bn + ( currPage * Constants.VW_NUM_ROWS));
      }
      return await SearchService.solrQuery(query, signal)
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: false
    }
  );

  /* Next */
  const {
    isRefetching: nextIsFetching,
    refetch: nextRefetch,
    isSuccess: nextIsSuccess,
    data: nextData
  } = useQuery<any, Error>(
    [viewerState.viewerRecord + '_next_' + viewerState.viewerRecord + currPage],
    async ({signal}) => {
      let query: string;
      if (viewerState.recordTool === Constants.BROWSE) {
        query = BrowseQuery(viewerState.initialRecord.collectioncode,  Constants.VW_NUM_ROWS, Constants.NEXT, viewerState.initialRecord.id_int + (currPage * Constants.VW_NUM_ROWS));
      } else {
        query = BatesQuery(viewerState.initialRecord.collectioncode,  Constants.VW_NUM_ROWS, Constants.NEXT, viewerState.initialRecord.bn + (currPage * Constants.VW_NUM_ROWS));
      }
      return await SearchService.solrQuery(query, signal)
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: false
    }
  );

  useEffect(()=>{
    if (prevData && prevData.response) {
      setPrevDisabled( prevData.response.docs.length < Constants.VW_NUM_ROWS);
    } else {
      setPrevDisabled( false );
    }

    if (nextData && nextData.response) {
      setNextDisabled( nextData.response.docs.length < Constants.VW_NUM_ROWS);
    } else {
      setNextDisabled( false );
    }
  }, [prevData, nextData])

  const updateScroll = (direction: string)=>{
    return new Promise(resolve => {
      setTimeout(()=> {
        if (cardRef.current && cardContainerRef.current && resultsContainer.current) {
          if (initialRender.current) {
            // center initial record for browse
            initialRender.current = false;
            if (prevData.response) {
              resultsContainer.current.scrollTop = cardRef.current.clientHeight * (prevData.response.docs.length - ((viewerState.recordTool === Constants.BROWSE) ? 0: 1))
            }
          } else {
            if (direction === Constants.NEXT) {
              resultsContainer.current.scrollTop = cardContainerRef.current.clientHeight - (cardRef.current.clientHeight * Constants.VW_NUM_ROWS) - 120;
            } else {
              resultsContainer.current.scrollTop = 0;
            }
          }
        }
        resolve(null);
      }, 100);
    })
  }

  useEffect(()=>{
    if (!viewerState.initialRecord) return;
    initialRender.current = true;

    setNumberingOffset(0);
    setCurrPage(0);
    setLoading(true);
    dispatch(clearResults())

    prevRefetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerState.recordTool, prevRefetch])

  useEffect(()=>{
    if (prevIsSuccess && prevData) {
      let _records: any[] = [...prevData.response.docs].reverse();
      if (initialRender.current) {
        _records.push({...viewerState.initialRecord});
      }
      //_records = _records.concat([...records])

      if (_records.length >((Constants.VW_NUM_ROWS * 2) + 1)) {
        _records.splice((Constants.VW_NUM_ROWS * 2) + 1, (_records.length - ((Constants.VW_NUM_ROWS * 2) + 1)));
      }

      const data: any = {};
      data.response = {};
      data.response.docs = _records;
      dispatch(setResults({db_set: Constants.DOCUMENTS, data}));

      if (initialRender.current) {
        // initial fetch
        setNumberingOffset(Constants.VW_NUM_ROWS - prevData.response.docs.length) // case less than VW_NUM_ROWS docs
        setNumOfRecords(prevData.response.numFound)

      } else {
        updateScroll(Constants.PREVIOUS).then(()=>setLoading(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevData, prevIsSuccess, setNumOfRecords])

  useEffect(()=>{
    if (prevIsSuccess && prevData) {
      nextRefetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsState.results]);

  useEffect(()=>{
    if (nextIsSuccess && nextData) {
      const _records: any[] = [...resultsState.results].concat([...nextData.response.docs])
      if (_records.length > ((Constants.VW_NUM_ROWS * 2) - 1)) {
        _records.splice(0, (_records.length - (Constants.VW_NUM_ROWS * 2) - 1));
      }

      const data: any = {};
      data.response = {};
      data.response.docs = _records;
      dispatch(setResults({db_set: Constants.DOCUMENT, data}));

      updateScroll(Constants.NEXT).then(()=>{
        setLoading(false)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextData, nextIsSuccess])

  const card = useCallback((result: any, index: number):JSX.Element => {
    return <Button
      ref={cardRef}
      key={`dc-${index}`}
      className={'viewer-card-wrapper mb-2 text-dark p-0 bg-transparent text-start w-100'}
      active={(viewerState.viewerRecord.id === result.id)}
      onClick={()=>handleSelectRecord(result)}
    >
      <ViewerCard
        index={index}
        record={result}
        page={1}
        resultsPerPage={Constants.VW_NUM_ROWS}
      />
    </Button>
  }, [handleSelectRecord, viewerState.viewerRecord.id]);

  const handlePrevious = useCallback(()=>{
    if (!prevIsFetching) {
      setLoading(true);
      setCurrPage(currPage - 1)
      setTimeout(()=>{
        prevRefetch()
      })
    }
  }, [currPage, prevRefetch, prevIsFetching])

  const handleNext = useCallback(()=>{
    if (!nextIsFetching) {
      setLoading(true);
      setCurrPage(currPage + 1)
      setTimeout(()=>{
        nextRefetch()
      })
    }
  }, [currPage, nextRefetch, nextIsFetching]);

  return (
    <div className={'viewer-bates-list'} >

      {/* Loading Spinner */}
      {loading &&
        <div className={'text-center pt-4'}>
          <Spinner size={Constants.SMALL}/>
        </div>}

      <div className={'cards d-flex flex-column pt-1 pb-2 overflow-scroll'} ref={resultsContainer}>

        {/* Cards */}
        {<div className={'h-100' + (loading ? ' hide-list': '')}>
          <BateBrowsePaging
            mode={Constants.PREVIOUS}
            handlePrevious={handlePrevious}
            prevDisabled={prevDisabled}
            handleNext={nextRefetch}/>

            <div className={'list-container'} ref={cardContainerRef}>
              <ul>
                {resultsState.results.map((result: any, index: number) => {
                  return <li key={index}>{card(result, index - ((-1 * currPage + 1) * Constants.VW_NUM_ROWS) + numberingOffset - 1) }</li>
                })}
              </ul>
            </div>

          <BateBrowsePaging
            mode={Constants.NEXT}
            nextDisabled={nextDisabled}
            handlePrevious={handlePrevious}
            handleNext={handleNext}/>
        </div>}

      </div>
    </div>
  )
}
export default ViewerBatesBrowseList;
